import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, X } from 'lucide-react';
import { CONTACT } from '../data/constants';
import { ProductExtras } from './ProductExtras';

// Reusable clickable "Call for Pricing" product grid + detail modal
const CallForPricingGrid = ({ eyebrow, title, subtitle, items, testId }) => {
  const [selected, setSelected] = useState(null);
  const phoneHref = `tel:${CONTACT.phone.replace(/[^0-9]/g, '')}`;

  const getImages = (item) => (Array.isArray(item.images) && item.images.length ? item.images : [item.image]).filter(Boolean);
  const metaLine = (item) => [item.series, item.type, item.capacity, item.category].filter(Boolean).join(' · ');

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="my-12 md:my-16"
      data-testid={testId}
    >
      <div className="mb-8">
        {eyebrow && (
          <span className="text-sm font-semibold text-[#F88200] uppercase tracking-wider">{eyebrow}</span>
        )}
        <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl lg:text-5xl font-black uppercase text-[#0E3D95] leading-none">
          {title}
        </h2>
        {subtitle && <p className="text-slate-600 mt-2 max-w-3xl">{subtitle}</p>}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
        {items.map((item) => (
          <button
            key={item.id}
            onClick={() => setSelected(item)}
            className="group text-left bg-white rounded-xl overflow-hidden shadow-lg border border-slate-100 flex flex-col hover:shadow-2xl hover:-translate-y-1 transition-all"
            data-testid={`cfp-card-${item.id}`}
          >
            <div className="aspect-[4/3] overflow-hidden bg-slate-50">
              <img src={getImages(item)[0]} alt={item.name} loading="lazy" className={`w-full h-full ${item.brand === 'Clearwater Spas' ? 'object-contain p-3' : 'object-cover'} group-hover:scale-105 transition-transform duration-500`} />
            </div>
            <div className="p-5 flex flex-col flex-1">
              <h3 className="font-['Barlow_Condensed'] text-xl md:text-2xl font-bold uppercase text-[#0E3D95]">{item.name}</h3>
              {metaLine(item) && (
                <p className="text-sm text-[#F88200] font-semibold mt-1">{metaLine(item)}</p>
              )}
              <p className="text-sm text-slate-600 mt-2 flex-1 line-clamp-3">{item.description}</p>
              <div className="mt-4 flex items-center justify-between gap-2 pt-3 border-t border-slate-100">
                <span className="font-bold text-[#F88200]">Call for Pricing</span>
                <span className="inline-flex items-center gap-1 text-[#0E3D95] font-semibold text-sm group-hover:text-[#F88200]">View Details →</span>
              </div>
            </div>
          </button>
        ))}
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setSelected(null)}
            data-testid="cfp-modal"
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative">
                <button
                  onClick={() => setSelected(null)}
                  className="absolute top-3 right-3 z-10 bg-white/90 hover:bg-white rounded-full p-2 shadow-md"
                  aria-label="Close"
                >
                  <X size={20} className="text-[#0E3D95]" />
                </button>
                <div className="grid md:grid-cols-2 gap-0">
                  <div className="bg-slate-50 p-4 flex flex-col gap-3">
                    {getImages(selected).map((img, i) => (
                      <img key={i} src={img} alt={`${selected.name} ${i + 1}`} className="w-full rounded-lg object-contain bg-white" />
                    ))}
                  </div>
                  <div className="p-6 md:p-8 flex flex-col">
                    <h3 className="font-['Barlow_Condensed'] text-3xl font-black uppercase text-[#0E3D95] leading-none">{selected.name}</h3>
                    {metaLine(selected) && <p className="text-[#F88200] font-semibold mt-2">{metaLine(selected)}</p>}
                    {selected.tagline && <p className="italic text-[#0E3D95] mt-2" data-testid="cfp-modal-tagline">{selected.tagline}</p>}
                    <p className="text-slate-600 mt-4">{selected.description}</p>
                    {Array.isArray(selected.specs) && selected.specs.length > 0 && (
                      <div className="mt-5 border border-slate-200 rounded-lg overflow-hidden">
                        {selected.specs.map((s, i) => (
                          <div key={i} className={`flex justify-between gap-4 px-4 py-2 text-sm ${i % 2 ? 'bg-white' : 'bg-slate-50'}`}>
                            <span className="text-slate-500">{s.label}</span>
                            <span className="text-[#0E3D95] font-semibold text-right">{s.value}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <ProductExtras item={selected} />
                    <p className="text-2xl font-bold text-[#F88200] mt-6">Call or Email for Pricing</p>
                    <div className="mt-4 flex flex-wrap gap-3">
                      <a href={phoneHref} className="btn-primary inline-flex items-center gap-2">
                        <Phone size={18} /> {CONTACT.phone}
                      </a>
                      <a href="/contact" className="border-2 border-[#0E3D95] text-[#0E3D95] hover:bg-[#0E3D95] hover:text-white px-6 py-3 rounded font-['Barlow_Condensed'] font-bold uppercase tracking-wider transition-colors">
                        Request Info
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.section>
  );
};

export default CallForPricingGrid;
